// PersonnelManagementView.tsx - Updated with portal-based dropdown to prevent overflow clipping
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { PersonnelManagementWrapper } from '../PersonnelManagement';
import { BulkPersonnelUpload } from './BulkPersonnelUpload';
import { PersonnelViewWrapper } from './PersonnelView';
import { PersonnelEditWrapper } from './PersonnelEditWrapper';
import { PersonnelDeleteConfirmationModal } from './PersonnelDeleteConfirmationModal';
import './PersonnelListView.css'; // Import personnel-specific styles
import '../EnterpriseTable.css'; 
import '../companyManagement/CompanyListOverrides.css';
import '../companyManagement/CompanyHelpPanel.css';

type PersonnelCreationStep = 'upload' | 'processing' | 'form' | 'saving';
type PersonnelModalMode = 'add' | 'edit' | 'view';

interface StatusUpdate {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
}

interface PersonnelRecord {
  personnelId: number;
  companyId: number;
  fullName: string;
  firstName?: string;
  lastName?: string;
  nationality?: string;
  passportNumber?: string;
  visaType?: string;
  dateOfBirth?: string;
  sex?: string;
  placeOfBirth?: string;
  nationalIdNo?: string;
  passportIssueDate?: string;
  passportExpiryDate?: string;
  typeOfVisa?: string;
  visaIssueDate?: string;
  visaExpiry?: string;
  bloodGroup?: string;
  photoUrl?: string;
  createdAt: string;
  updatedAt: string;
}



interface UserInfo {
  userType: 'company' | 'gate' | 'admin';
  companyId?: number;
  companyName?: string;
  userName?: string;
  userEmail?: string;
}

interface PersonnelManagementViewProps {
  getAccessToken: () => Promise<string>;
  onStatusUpdate: (status: StatusUpdate) => void;
  userInfo?: UserInfo;
}

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

export const PersonnelManagementView: React.FC<PersonnelManagementViewProps> = ({
  getAccessToken,
  onStatusUpdate,
  userInfo
}) => {
  // Personnel-specific state
//  const [personnelView, setPersonnelView] = useState<'table' | 'add' | 'edit' | 'view'>('table');
  const [personnelView, setPersonnelView] = useState<'table' | 'add' | 'edit' | 'view' | 'edit-form' | 'bulk'>('table');

  const [personnelModalMode, setPersonnelModalMode] = useState<PersonnelModalMode>('add');
  const [viewingPersonnel, setViewingPersonnel] = useState<PersonnelRecord | null>(null);
  const [personnelRecords, setPersonnelRecords] = useState<PersonnelRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [helpVisible, setHelpVisible] = useState(true);
  const [showActionHint, setShowActionHint] = useState(false);
  const [revealedRow, setRevealedRow] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; openUpward: boolean } | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [personnelCreationStep, setPersonnelCreationStep] = useState<PersonnelCreationStep>('upload');

  // Refs for portal dropdown
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const portalDropdownRef = useRef<HTMLDivElement>(null);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    personnelId: string;
    personnelName: string;
  }>({
    isOpen: false,
    personnelId: '',
    personnelName: ''
  });




  // Title-case helper — converts "AHMED ABDALLA" → "Ahmed Abdalla"
  const toTitleCase = (str: string | undefined): string => {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Filter personnel based on search term
  const filteredPersonnel = useMemo(() => {
    if (!searchTerm.trim()) {
      return personnelRecords;
    }

    const term = searchTerm.toLowerCase().trim();
    
    return personnelRecords.filter(person => 
      person.fullName.toLowerCase().includes(term) ||
      person.nationality?.toLowerCase().includes(term) ||
      person.passportNumber?.toLowerCase().includes(term) ||
      person.visaType?.toLowerCase().includes(term)
    );
  }, [personnelRecords, searchTerm]);

  // Close dropdown on scroll or resize (portal is position:fixed)
  useEffect(() => {
    if (!openDropdownId) return;
    
    const closeDropdown = () => {
      setOpenDropdownId(null);
      setDropdownPosition(null);
    };

    window.addEventListener('scroll', closeDropdown, true);
    window.addEventListener('resize', closeDropdown);
    return () => {
      window.removeEventListener('scroll', closeDropdown, true);
      window.removeEventListener('resize', closeDropdown);
    };
  }, [openDropdownId]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const buttonElement = openDropdownId ? dropdownButtonRefs.current.get(openDropdownId) : null;
      const portalElement = portalDropdownRef.current;

      if (buttonElement && !buttonElement.contains(target) &&
          (!portalElement || !portalElement.contains(target))) {
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdownId]);

  // Load personnel records from API
  const loadPersonnelRecords = useCallback(async () => {
    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/personnel`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPersonnelRecords(data.data || []);
        
      } else {
        onStatusUpdate({
          type: 'error',
          message: 'Failed to load personnel records'
        });
      }
    } catch (error) {
      console.error('Error loading personnel:', error);
      onStatusUpdate({
        type: 'error',
        message: 'Network error while loading personnel records'
      });
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, onStatusUpdate]);

  // Load personnel records on component mount
  useEffect(() => {
    if (personnelView === 'table' && personnelRecords.length === 0) {
      loadPersonnelRecords();
    }
  }, [personnelView, personnelRecords.length, loadPersonnelRecords]);

  // Toggle dropdown - calculates position for portal rendering
  const toggleDropdown = (personnelId: string) => {
    if (openDropdownId === personnelId) {
      setOpenDropdownId(null);
      setDropdownPosition(null);
    } else {
      const buttonEl = dropdownButtonRefs.current.get(personnelId);
      if (buttonEl) {
        const rect = buttonEl.getBoundingClientRect();
        const menuHeight = 160;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < menuHeight;

        setOpenDropdownId(personnelId);
        setDropdownPosition({
          top: openUpward ? rect.top : rect.bottom + 4,
          left: rect.right,
          openUpward
        });
      }
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Auto-dismiss action hint
  useEffect(() => {
    if (!showActionHint) return;
    const timer = setTimeout(() => setShowActionHint(false), 6000);
    return () => clearTimeout(timer);
  }, [showActionHint]);

  useEffect(() => {
    if (openDropdownId) setShowActionHint(false);
  }, [openDropdownId]);

  // Handle personnel creation step changes
  const handlePersonnelStepChange = useCallback((step: PersonnelCreationStep) => {
    setPersonnelCreationStep(step);
  }, []);

  // Handler for operations that modify data (add/edit) - RELOADS data
  const handlePersonnelDataModified = useCallback(() => {
    setPersonnelView('table');
    setPersonnelModalMode('add');
    setViewingPersonnel(null);
    setPersonnelCreationStep('upload');
    sessionStorage.removeItem('editingPersonnel');
    // Reload data since it was modified
    loadPersonnelRecords();
  }, [loadPersonnelRecords]);

  // Handler for view-only operations - NO RELOAD
  const handlePersonnelViewClosed = useCallback(() => {
    setPersonnelView('table');
    setPersonnelModalMode('add');
    setViewingPersonnel(null);
    setPersonnelCreationStep('upload');
    sessionStorage.removeItem('editingPersonnel');
    // NO data reload - just return to table view
  }, []);

  // Handler for general navigation - NO RELOAD unless coming from add/edit
  const handlePersonnelListUpdate = useCallback(() => {
    const wasModifying = personnelModalMode === 'add' || personnelModalMode === 'edit';
    
    setPersonnelView('table');
    setPersonnelModalMode('add');
    setViewingPersonnel(null);
    setPersonnelCreationStep('upload');
    sessionStorage.removeItem('editingPersonnel');
    
    // Only reload if we were in a modifying mode
    if (wasModifying) {
      loadPersonnelRecords();
    }
  }, [personnelModalMode, loadPersonnelRecords]);

  // Personnel action handlers
  const handleAddPersonnel = () => {
    setPersonnelModalMode('add');
    setViewingPersonnel(null);
    setPersonnelView('add');
    setPersonnelCreationStep('upload');
  };

  // Open the bulk Excel + ZIP upload view
  const handleBulkUpload = () => {
    setPersonnelView('bulk');
  };

  // Bulk import finished — return to the table and reload to show imported records
  const handleBulkComplete = useCallback(() => {
    setPersonnelView('table');
    loadPersonnelRecords();
  }, [loadPersonnelRecords]);



 const handleEditPersonnel = (personnel: PersonnelRecord) => {
  setOpenDropdownId(null);
  setDropdownPosition(null);
  setPersonnelModalMode('edit');
  setViewingPersonnel(personnel);
  sessionStorage.setItem('editingPersonnel', JSON.stringify(personnel));
  setPersonnelView('edit-form'); // Changed from 'edit' to 'edit-form'
};

  const handleViewDetails = (personnel: PersonnelRecord) => {
    setOpenDropdownId(null);
    setDropdownPosition(null);
    setPersonnelModalMode('view');
    setViewingPersonnel(personnel);
    setPersonnelView('view');
    setPersonnelCreationStep('form');
    
    onStatusUpdate({
      type: 'info',
      message: `Viewing details for ${personnel.fullName}`
    });
  };
const handleEditSave = useCallback((updatedPersonnel: PersonnelRecord) => {
  // Update the personnel record in local state
  setPersonnelRecords(prev => 
    prev.map(p => p.personnelId === updatedPersonnel.personnelId ? updatedPersonnel : p)
  );
  
  // Reset view state
  setPersonnelView('table');
  setPersonnelModalMode('add');
  setViewingPersonnel(null);
  sessionStorage.removeItem('editingPersonnel');
  
  // Reload data to ensure consistency
  loadPersonnelRecords();
}, [loadPersonnelRecords]);
  // Delete handler to show custom modal
  const handleDeletePersonnel = (personnel: PersonnelRecord) => {
    setOpenDropdownId(null);
    setDropdownPosition(null);
    
    // Show custom delete confirmation modal
    setDeleteModal({
      isOpen: true,
      personnelId: String(personnel.personnelId),
      personnelName: personnel.fullName
    });
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    const { personnelId } = deleteModal;
    
    // Close the modal first
    setDeleteModal({
      isOpen: false,
      personnelId: '',
      personnelName: ''
    });

    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/personneldelete/${personnelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        onStatusUpdate({
          type: 'success',
          message: 'Personnel record deleted successfully'
        });
        // Force reload after deletion
        loadPersonnelRecords();
      } else {
        onStatusUpdate({
          type: 'error',
          message: 'Failed to delete personnel record'
        });
      }
    } catch (error) {
      onStatusUpdate({
        type: 'error',
        message: 'Network error while deleting personnel record'
      });
    }
  };

  // Handle delete cancellation
  const handleCancelDelete = () => {
    setDeleteModal({
      isOpen: false,
      personnelId: '',
      personnelName: ''
    });
  };

  // Helper function to mask passport number
  const maskPassportNumber = (passportNumber: string | undefined): string => {
    if (!passportNumber || passportNumber.trim() === '') {
      return 'N/A';
    }
    
    const trimmed = passportNumber.trim();
    
    if (trimmed.length <= 3) {
      return trimmed;
    }
    
    const visiblePart = trimmed.substring(0, 3);
    const maskedPart = '*'.repeat(trimmed.length - 3);
    
    return visiblePart + maskedPart;
  };

  // Compute document expiry status — returns null when valid (nothing to flag)
  const getExpiryStatus = (dateStr: string | undefined): { label: string; className: string } | null => {
    if (!dateStr) return null;

    const expiry = new Date(dateStr);
    if (isNaN(expiry.getTime())) return null;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'Expired', className: 'expiry-expired' };
    if (diffDays <= 14) return { label: `${diffDays}d left`, className: 'expiry-warning' };
    return null; // Valid — no badge needed
  };

  // Render personnel table
  const renderPersonnelTable = () => {
    if (loading) {
      return (
        <div className="loading">
          <span className="spinner"></span>
          Loading personnel records...
        </div>
      );
    }

    if (personnelRecords.length === 0) {
      return (
        <div className="company-management">
           <h1 style={{
            fontSize: '25px',
            fontWeight: 600,
            color: '#1a1a1a',
            margin: '0 0 12px 0',
            padding: '5px 0 0 20px',
            lineHeight: 1.3,
            fontFamily: "'Segoe UI', sans-serif",
            letterSpacing: '-0.01em',
          }}>
            Personnel Management
          </h1>
          <div className="no-companies">
            <div className="no-data-icon">👥</div>
            <h3>No Personnel Records Found</h3>
            <p>Start by adding your first personnel record to the system.</p>
            <button onClick={handleAddPersonnel} className="create-first-btn">
              Add First Personnel Record
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="company-management">
        <div className={`cm-layout ${!helpVisible ? 'help-collapsed' : ''}`}>
          <div className="cm-main">
             <h1 style={{
          fontSize: '25px',
          fontWeight: 600,
          color: '#1a1a1a',
          margin: '0 0 12px 0',
          padding: '5px 0 0 20px',
          lineHeight: 1.3,
          fontFamily: "'Segoe UI', sans-serif",
          letterSpacing: '-0.01em',
        }}>
          Personnel Management
        </h1>
        <div className="companies-container">
          {/* Toolbar */}
          <div className="table-toolbar">
            <div className="toolbar-left">
              <span className="record-count">{personnelRecords.length} personnel</span>
            </div>
            <div className="toolbar-right">
              <div className="search-field">
                <svg className="search-icon-svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search personnel..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button onClick={clearSearch} className="clear-search-btn" title="Clear search">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
              <button onClick={handleBulkUpload} className="create-company-btn" style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db' }} title="Bulk upload pass holders from Excel + ZIP">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span>Bulk Upload</span>
              </button>
              <button onClick={handleAddPersonnel} className="create-company-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Add Personnel</span>
              </button>
            </div>
          </div>

          {searchTerm && (
            <div className="search-results-bar">
              Showing {filteredPersonnel.length} of {personnelRecords.length} results for &ldquo;<strong>{searchTerm}</strong>&rdquo;
              <button onClick={clearSearch} className="clear-filter-btn">Clear filter</button>
            </div>
          )}

          <div className="companies-table-container">
            <table className="enterprise-table">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Nationality</th>
                  <th>Passport No.</th>
                  <th>Alerts</th>
                  <th className="th-actions"></th>
                </tr>
              </thead>
              <tbody>
                {filteredPersonnel.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="no-results">
                      <div className="no-results-content">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <p>No personnel found matching your search</p>
                        <button onClick={clearSearch} className="clear-search-link">Clear search</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPersonnel.map((person, rowIndex) => {
                    const personId = String(person.personnelId);
                    
                    return (
                      <tr
                        key={personId}
                        className="clickable-row"
                        onClick={() => handleViewDetails(person)}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleViewDetails(person); }}
                      >
                        <td className="td-name">
                          <div className="name-details">
                            <span className="org-name-text" style={{ userSelect: 'none' as const }}>
                              {revealedRow === personId ? '•'.repeat((toTitleCase(person.fullName) || 'N/A').length) : (toTitleCase(person.fullName) || 'N/A')}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="region-chip default">{toTitleCase(person.nationality) || 'N/A'}</span>
                        </td>
                        <td style={{ color: '#374151', fontSize: '13px' }}>
                          <span
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            title="Press and hold to reveal"
                            onMouseDown={(e) => { e.stopPropagation(); e.currentTarget.textContent = person.passportNumber?.trim() || 'N/A'; setRevealedRow(personId); }}
                            onMouseUp={(e) => { e.currentTarget.textContent = maskPassportNumber(person.passportNumber); setRevealedRow(null); }}
                            onMouseLeave={(e) => { e.currentTarget.textContent = maskPassportNumber(person.passportNumber); setRevealedRow(null); }}
                          >
                            {maskPassportNumber(person.passportNumber)}
                          </span>
                        </td>
                        <td>
                          <div className="doc-status-badges">
                            {(() => {
                              const passport = getExpiryStatus(person.passportExpiryDate);
                              const visa = getExpiryStatus(person.visaExpiry);
                              if (!passport && !visa) {
                                return <span className="doc-status-clear">&mdash;</span>;
                              }
                              return (
                                <>
                                  {passport && (
                                    <span className={`expiry-badge ${passport.className}`} title={`Passport expires: ${new Date(person.passportExpiryDate!).toLocaleDateString()}`}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="2" y1="9" x2="22" y2="9"/></svg>
                                      PP: {passport.label}
                                    </span>
                                  )}
                                  {visa && (
                                    <span className={`expiry-badge ${visa.className}`} title={`Visa expires: ${new Date(person.visaExpiry!).toLocaleDateString()}`}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                      Visa: {visa.label}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="td-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="action-dropdown">
                            {rowIndex === 0 && showActionHint && (
                              <div className="action-hint-tooltip" onClick={() => setShowActionHint(false)}>
                                <span>Click to view, edit, or delete</span>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                              </div>
                            )}
                            <button
                              ref={(el) => {
                                if (el) {
                                  dropdownButtonRefs.current.set(personId, el);
                                } else {
                                  dropdownButtonRefs.current.delete(personId);
                                }
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleDropdown(personId);
                              }}
                              className={`action-trigger ${rowIndex === 0 && showActionHint ? 'hint-pulse' : ''}`}
                              title="More actions"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <circle cx="12" cy="5" r="2" />
                                <circle cx="12" cy="12" r="2" />
                                <circle cx="12" cy="19" r="2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
          </div>

          {/* Right: Help Guide — animated collapse */}
          <div
            className="cm-help-right"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              alignSelf: 'flex-start',
              width: helpVisible ? 310 : 44,
              padding: '28px 0 32px 0',
              transition: 'width 0.3s ease',
              overflow: 'hidden',
            }}
          >
            <div
              className="cm-help-content"
              style={{
                flex: helpVisible ? 1 : '0 0 0px',
                opacity: helpVisible ? 1 : 0,
                overflow: 'hidden',
                transition: 'opacity 0.25s ease, flex 0.3s ease',
                pointerEvents: helpVisible ? 'auto' : 'none',
                minWidth: 0,
                padding: helpVisible ? '0 16px 0 28px' : '0',
              }}
            >
              <div className="cm-help-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <p className="cm-help-title">Personnel Management</p>
              <p className="cm-help-text">Manage personnel records for gate pass requests. Each person needs a valid record before they can be added to a gate pass.</p>
              <p className="cm-help-subtitle">Adding Personnel</p>
              <p className="cm-help-text">Upload a passport or ID document — the system will extract details automatically using AI-powered OCR.</p>
              <p className="cm-help-subtitle">Passport Privacy</p>
              <p className="cm-help-text">Passport numbers are masked in the table view for security. Full details are visible in the detail view.</p>
              <p className="cm-help-subtitle">Quick Actions</p>
              <p className="cm-help-text">Click any row to view full details with action buttons inside. Or use the three-dot menu for quick access to edit or delete a personnel record.</p>
            </div>

            <button
              type="button"
              onClick={() => setHelpVisible(!helpVisible)}
              aria-label={helpVisible ? 'Hide Guide' : 'Show Guide'}
              title={helpVisible ? 'Hide Guide' : 'Show Guide'}
              style={helpVisible ? {
                display: 'flex', alignItems: 'center', gap: 6,
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                background: '#f5f5f5', border: '1px solid #e0e0e0', borderRight: 'none',
                borderRadius: '6px 0 0 6px', padding: '12px 7px',
                fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
                letterSpacing: '0.02em', flexShrink: 0, alignSelf: 'flex-start',
                marginTop: 36, zIndex: 2, transition: 'background 0.15s ease',
              } : {
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, borderRadius: '50%',
                border: '1.5px solid #d4d4d4', background: '#ffffff', cursor: 'pointer',
                flexShrink: 0, margin: '6px auto 0 auto', padding: 0,
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (helpVisible) { e.currentTarget.style.background = '#ebebeb'; }
                else { e.currentTarget.style.borderColor = '#F3694A'; e.currentTarget.style.background = '#fef7f5'; }
              }}
              onMouseLeave={(e) => {
                if (helpVisible) { e.currentTarget.style.background = '#f5f5f5'; }
                else { e.currentTarget.style.borderColor = '#d4d4d4'; e.currentTarget.style.background = '#ffffff'; }
              }}
            >
              {helpVisible ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: 'rotate(90deg)' }}>
                    <circle cx="12" cy="12" r="10" /><path d="M15 9l-6 3 6 3" />
                  </svg>
                  <span>Hide Guide</span>
                </>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#848d9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Main render method
  if (personnelView === 'table') {
    return (
      <>
        {renderPersonnelTable()}

        {/* Portal-rendered Dropdown Menu - renders outside DOM hierarchy to avoid overflow clipping */}
        {openDropdownId && dropdownPosition && createPortal(
          <div
            ref={portalDropdownRef}
            className="personnel-portal-dropdown-menu"
            style={{
              position: 'fixed',
              top: dropdownPosition.openUpward ? 'auto' : `${dropdownPosition.top}px`,
              bottom: dropdownPosition.openUpward ? `${window.innerHeight - dropdownPosition.top + 4}px` : 'auto',
              left: `${dropdownPosition.left}px`,
              transform: 'translateX(-100%)',
            }}
          >
            {(() => {
              const activePerson = filteredPersonnel.find(p => String(p.personnelId) === openDropdownId);
              if (!activePerson) return null;
              return (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(activePerson);
                    }}
                    className="personnel-portal-dropdown-item"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    View Details
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditPersonnel(activePerson);
                    }}
                    className="personnel-portal-dropdown-item"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Edit
                  </button>
                  <div className="personnel-portal-dropdown-divider"></div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePersonnel(activePerson);
                    }}
                    className="personnel-portal-dropdown-item delete"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    Delete
                  </button>
                </>
              );
            })()}
          </div>,
          document.body
        )}
        
        {/* Custom Personnel Delete Confirmation Modal */}
        <PersonnelDeleteConfirmationModal
          isOpen={deleteModal.isOpen}
          personnelName={deleteModal.personnelName}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </>
    );
  }

  // Bulk Excel + ZIP upload view
  if (personnelView === 'bulk') {
    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(17, 24, 39, 0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
        }}
        onClick={() => setPersonnelView('table')}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <BulkPersonnelUpload
            getAccessToken={getAccessToken}
            onClose={() => setPersonnelView('table')}
            onComplete={handleBulkComplete}
            onStatusUpdate={onStatusUpdate}
            usage="management"
          />
        </div>
      </div>
    );
  }

  // ADD THIS NEW SECTION RIGHT HERE:
 if (personnelView === 'edit-form') {
  return (
    <>
      <PersonnelEditWrapper 
        personnelData={viewingPersonnel}
        onClose={handlePersonnelListUpdate}
        onSave={handleEditSave}
        getAccessToken={getAccessToken}
        onStatusUpdate={onStatusUpdate}
      />

      <PersonnelDeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        personnelName={deleteModal.personnelName}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
}


  
  // Render the modal directly — PersonnelManagementWrapper and PersonnelViewWrapper
  // each render their own modal-overlay/modal-container (matching OrganizationForm pattern).
  // No outer wrapper needed — this eliminates the animated double-wrapper that caused
  // the rightward shift on mount.
  return (
    <>
      {personnelModalMode === 'view' ? (
        <PersonnelViewWrapper 
          personnelData={viewingPersonnel}
          onClose={handlePersonnelViewClosed}
          onEdit={handleEditPersonnel}
          onDelete={handleDeletePersonnel}
        />
      ) : (
        <PersonnelManagementWrapper 
          getAccessToken={getAccessToken}
          onPersonnelListUpdate={handlePersonnelDataModified}
          onStepChange={handlePersonnelStepChange}
        />
      )}

      {/* Custom Personnel Delete Confirmation Modal */}
      <PersonnelDeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        personnelName={deleteModal.personnelName}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};